import { useRef, useState, type ReactElement } from 'react';
import { Clip, DATEIWAHL, type Op, freieStelle, istGespraech, sortedClips, timelineDuration } from '@schnitt/shared';
import { api, type AssetView } from '../api';
import { ausDatei, useEditor } from '../store';
import { newId } from '../ids';
import { Sheet } from './Sheet';
import { MedienHolenSheet } from './MedienHolenSheet';
import { Icon } from './Icon';
import { ContextMenu, type MenuItem } from './ContextMenu';
import { WorkerWarning, useWorkerAlive } from './WorkerWarning';
import { LeererZustand } from './LeererZustand';
import { vergiss } from '../vorladen';
import { useZeitText } from '../zeit';
import { anDieReihe } from '../einfuegeWarteschlange';
import { GeraeteAuswahl } from './GeraeteAuswahl';
import { MedienVorschau } from './MedienVorschau';
import { ablegen, hochladen, starteHochladen, type Lauf } from '../hochladen';

/**
 * Eine Adresse herunterladen, ohne die Seite zu verlassen: der Server sagt
 * „Anhang“, also bleibt der Schnitt stehen und der Browser speichert.
 */
function herunterladen(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
}

function groesseText(bytes: number): string {
  return bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : `${Math.max(0.1, bytes / 1024 ** 2).toFixed(1)} MB`;
}

/**
 * The media bin: everything the project can cut with, and what state it is in.
 * Material arrives either from the project's Box folder or straight from the
 * device -- a project does not have to have a folder. Tapping a clip appends it
 * to the end of the matching track, which is what people do ninety per cent of
 * the time; the timeline is for the other ten.
 */
export function MediaSheet({
  onClose,
  onRecord,
  onDiashow,
}: {
  onClose: () => void;
  onRecord: () => void;
  onDiashow: () => void;
}): ReactElement {
  const zeit = useZeitText();
  const assets = useEditor((s) => s.assets);
  const project = useEditor((s) => s.project);
  const timeline = useEditor((s) => s.timeline);
  const dispatch = useEditor((s) => s.dispatch);
  const toast = useEditor((s) => s.toast);
  const refreshAssets = useEditor((s) => s.refreshAssets);
  const role = useEditor((s) => s.role);
  // 7.14: Kommt man über „Medium ersetzen“ aus dem Kontextmenü, ist dieselbe
  // Liste eine Auswahl statt einer Ablage.
  const ersetzen = useEditor((s) => s.ersetzen);
  const setErsetzen = useEditor((s) => s.setErsetzen);
  const stelle = useEditor((s) => s.einfuegeStelle);
  const einfuegeSpur = useEditor((s) => s.einfuegeSpur);
  const setEinfuegeStelle = useEditor((s) => s.setEinfuegeStelle);
  const setPlatzieren = useEditor((s) => s.setPlatzieren);
  const setEinfuegeMedium = useEditor((s) => s.setEinfuegeMedium);
  const zuErsetzen = ersetzen ? timeline.tracks.flatMap((t) => t.clips).find((c) => c.id === ersetzen) : undefined;
  const [syncing, setSyncing] = useState(false);
  /** Das Blatt zum Holen aus einem anderen Ordner (7.11). */
  const [holen, setHolen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'video' | 'audio' | 'image'>('all');
  /**
   * Nach dem Namen suchen (19. September).
   *
   * Aus Saschas Bildschirmfoto vom 18. September: dort steht über der
   * Medienliste ein Feld „In Medien suchen …“. Bei uns gab es nur die vier
   * Chips für die Art -- bei zwölf Dateien reicht das, bei hundertzwanzig
   * nicht, und lange Projekte haben hundertzwanzig.
   *
   * Nur im Browser gefiltert und nicht über den Server: die Liste ist ohnehin
   * schon da, und eine Anfrage je Tastendruck wäre eine Leitung mehr für
   * nichts.
   */
  const [suche, setSuche] = useState('');
  const picker = useRef<HTMLInputElement>(null);
  const upload = useEditor((s) => s.hochladen);
  const [zipLaeuft, setZipLaeuft] = useState(false);
  /**
   * Was gerade vom Gerät ausgesucht wurde: es lädt schon hoch (4. November),
   * offen sind nur noch Reihenfolge und Ziel.
   */
  const [auswahl, setAuswahl] = useState<{ dateien: File[]; lauf: Lauf } | null>(null);
  const playhead = useEditor((s) => s.playhead);
  const [menu, setMenu] = useState<{ asset: AssetView; x: number; y: number } | null>(null);
  /** Was gerade über den ganzen Schirm gezeigt wird (16. Oktober). */
  const [ansehen, setAnsehen] = useState<AssetView | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  const [details, setDetails] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Only while something is actually waiting: no point asking otherwise.
  const waiting = assets.some((a) => a.status !== 'ready' && a.status !== 'failed');
  const worker = useWorkerAlive(waiting);

  const rename = async (asset: AssetView, name: string) => {
    setRenaming(null);
    if (!name.trim() || name.trim() === asset.name) return;
    try {
      await api.renameAsset(asset.id, name.trim());
      await refreshAssets();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Umbenennen fehlgeschlagen.');
    }
  };

  const remove = async (asset: AssetView) => {
    setBusy(true);
    try {
      await api.deleteAsset(asset.id);
      setConfirmDelete(null);
      // Die Liste zieht mit (7.83): sonst läuft das Vorladen mit der alten
      // Liste weiter und holt gleich darauf eine Datei, die es nicht mehr
      // gibt -- und der Zwischenspeicher hielte sie bis zum Projektwechsel.
      await vergiss([asset.proxyUrl, asset.originalUrl, asset.posterUrl, asset.filmstripUrl, asset.waveformUrl, asset.szenenUrl]);
      await refreshAssets();
      toast('info', `„${asset.name}“ gelöscht.`);
    } catch (err) {
      setConfirmDelete(null);
      toast('error', err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const reingest = async (asset: AssetView) => {
    try {
      await api.reingest(asset.id);
      await refreshAssets();
      toast('info', `„${asset.name}“ wird noch einmal aufbereitet.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Erneut versuchen fehlgeschlagen.');
    }
  };

  const menuItems = (asset: AssetView): MenuItem[] => {
    const items: MenuItem[] = [];
    if (asset.status === 'ready' && role !== 'viewer') {
      items.push({
        icon: 'plus',
        label: zuErsetzen ? 'Hier einsetzen' : stelle !== null ? `Bei ${zeit(stelle)} in den Film` : 'In den Schnitt einfügen',
        onSelect: () => insert(asset),
      });
      // Das fragende Blatt (7.49) auch ohne Stelle: seit „Hinzufügen“ (17. Oktober)
      // kommt man meist so hierher, und dann gilt der Abspielkopf.
      if (!zuErsetzen) items.push({ icon: 'plus', label: 'Wo einfügen …', onSelect: () => wohin(asset) });
    }
    if (role !== 'viewer') {
      items.push({ icon: 'text', label: 'Umbenennen', onSelect: () => setRenaming({ id: asset.id, value: asset.name }) });
      items.push({ icon: 'transition', label: 'Erneut aufbereiten', onSelect: () => void reingest(asset) });
    }
    items.push({ icon: 'sliders', label: details === asset.id ? 'Angaben ausblenden' : 'Angaben', onSelect: () => setDetails(details === asset.id ? null : asset.id) });
    if (asset.originalUrl) {
      // Als Datei mit ihrem Namen, nicht in einem neuen Fenster, in dem ein
      // Video bloss abspielt (Sascha, 15. Oktober: „Medien Download“).
      items.push({
        icon: 'export',
        label: 'Herunterladen',
        onSelect: () => herunterladen(`${asset.originalUrl}&als=${encodeURIComponent(asset.name)}`),
      });
    }
    if (role !== 'viewer') {
      items.push({ icon: 'trash', label: 'Löschen', danger: true, onSelect: () => setConfirmDelete(asset.id) });
    }
    return items;
  };

  /**
   * Hochladen steht in `hochladen.ts` -- dieselbe Strecke nimmt, was mit der
   * Maus abgelegt wird. Hier bleibt, was nur das Blatt angeht: kam man über
   * das `+` (7.156) oder wählte „direkt in den Film“, ist danach die Stelle
   * verbraucht und das Blatt zu.
   */
  const uploadFiles = async (list: File[], kopf: number | null) => {
    const ids = await hochladen(list, kopf, einfuegeSpur);
    if (kopf !== null && ids.length) {
      setEinfuegeStelle(null);
      onClose();
    }
  };

  /** Die Medien als ZIP, so wie die Liste gerade gefiltert ist (15. Oktober). */
  const zip = async () => {
    if (!project) return;
    setZipLaeuft(true);
    try {
      const { url, anzahl, groesse } = await api.medienZip(project.id, { art: filter, suche });
      herunterladen(url);
      toast('info', `ZIP mit ${anzahl} ${anzahl === 1 ? 'Datei' : 'Dateien'} (${groesseText(groesse)}) — der Download beginnt.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Das ZIP ging nicht.');
    } finally {
      setZipLaeuft(false);
    }
  };

  const sync = async () => {
    if (!project) return;
    setSyncing(true);
    try {
      const result = await api.syncBox(project.id);
      await refreshAssets();
      // Woher das Material kommt, hängt am Projekt: seit 7.50 kann das der
      // Rohdaten-Ordner der Ablage sein. Eine Meldung, die immer „aus Box“
      // sagt, sagt dann etwas Falsches.
      const woher = project.ablage ? (project.ablage.anbieter === 'nextcloud' ? 'aus Nextcloud' : 'aus der Ablage') : 'aus Box';
      const ordner = project.ablage ? 'im Ordner „rohdaten“' : 'im Box-Ordner';
      toast(
        'success',
        result.added > 0
          ? `${result.added} neue Datei${result.added === 1 ? '' : 'en'} ${woher} übernommen.`
          : `Keine neuen Dateien ${ordner}.`,
      );
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Der Abgleich ging nicht.');
    } finally {
      setSyncing(false);
    }
  };

  /** Das fragende Blatt (7.49): über dem Bild, im Film, nach dem Clip. */
  const wohin = (asset: AssetView) => {
    const dauer = asset.kind === 'image' ? 4 : Math.min(asset.durationSec, 3600);
    if (!(dauer > 0)) {
      toast('error', 'Dieses Medium hat keine verwertbare Länge.');
      return;
    }
    setEinfuegeMedium({ assetId: asset.id, name: asset.name, kind: asset.kind, dauer });
    onClose();
  };

  const insert = (asset: AssetView) => {
    if (asset.status !== 'ready') {
      toast('info', 'Dieses Medium wird noch aufbereitet.');
      return;
    }
    if (zuErsetzen) {
      // Der Clip bleibt, wo er ist, und behält seine Länge: ersetzt wird das
      // Material darunter, nicht der Schnitt. Reicht das neue Medium nicht so
      // weit, wird der Clip kürzer -- alles andere wäre ein Clip, der ins
      // Leere zeigt.
      const laenge = asset.kind === 'image' ? zuErsetzen.duration : Math.min(asset.durationSec, zuErsetzen.duration);
      if (laenge <= 0) {
        toast('error', 'Dieses Medium hat keine verwertbare Länge.');
        return;
      }
      dispatch({
        op: 'updateClip',
        clipId: zuErsetzen.id,
        patch: { source: { kind: 'asset', assetId: asset.id }, sourceIn: 0, duration: laenge, label: asset.name },
      } as Op);
      setErsetzen(null);
      toast(
        'success',
        laenge < zuErsetzen.duration
          ? `„${asset.name}“ eingesetzt — der Clip ist jetzt ${laenge.toFixed(1)} s kurz.`
          : `„${asset.name}“ eingesetzt.`,
      );
      onClose();
      return;
    }
    const isAudio = asset.kind === 'audio';
    const trackId = isAudio
      ? (timeline.tracks.find((t) => t.kind === 'audio')?.id ?? 'A1')
      : (timeline.tracks.find((t) => t.kind === 'video')?.id ?? 'V1');
    const track = timeline.tracks.find((t) => t.id === trackId);
    const clips = track ? sortedClips(track) : [];
    const hinten = clips.length ? clips.at(-1)!.start + clips.at(-1)!.duration : 0;
    const duration = asset.kind === 'image' ? 4 : Math.min(asset.durationSec, 3600);
    if (duration <= 0) {
      toast('error', 'Dieses Medium hat keine verwertbare Länge.');
      return;
    }
    // Kam der Weg hierher über das `+` auf der Abspiellinie, geht es **gleich
    // in den Film, am Marker**, und was dahinter liegt, rückt nach (S46) --
    // wie seit 7.156 aus der Mediathek. Das fragende Blatt (7.49, Saschas
    // „dann fragen wo hin“ vom 7. September) bleibt über „Wo einfügen …“ im
    // Menü der Zeile erreichbar, für den Fall, dass es über das Bild soll.
    if (stelle !== null) {
      anDieReihe([asset.id], stelle, einfuegeSpur);
      setEinfuegeStelle(null);
      onClose();
      return;
    }
    // Ohne Stelle (über „Hinzufügen“ gekommen): das Blatt geht zu, und die
    // Zeitleiste fragt mit ihren Plus, wohin (17. Oktober).
    if (role !== 'viewer') {
      setPlatzieren({ art: 'medien', assetIds: [asset.id], namen: [asset.name] });
      onClose();
      return;
    }
    const start = hinten;

    const clip = Clip.parse({
      id: newId('c'),
      source: { kind: 'asset', assetId: asset.id },
      start,
      duration,
      label: asset.name,
    });
    dispatch(ausDatei({ op: 'addClip', trackId, clip } as Op));
    toast('success', `„${asset.name}“ eingefügt.`);
  };

  // Gesucht wird ohne Rücksicht auf Gross- und Kleinschreibung und irgendwo im
  // Namen: wer „bahnhof“ tippt, meint auch „B-Roll_Bahnhof_02.mp4“.
  const gesucht = suche.trim().toLowerCase();
  const visible = assets.filter(
    (a) => (filter === 'all' || a.kind === filter) && (gesucht === '' || a.name.toLowerCase().includes(gesucht)),
  );
  // Nur fertige Bilder mit Massen -- ohne die lässt sich nicht sagen, wie eines
  // den Rahmen füllt (S23).
  const bilderFertig = assets.filter((a) => a.kind === 'image' && a.status === 'ready' && a.width > 0);
  const duration = timelineDuration(timeline);

  if (auswahl) {
    const { dateien, lauf } = auswahl;
    return (
      <Sheet
        title={dateien.length > 1 ? 'Reihenfolge und Ziel' : 'Wohin damit?'}
        // Schliessen heisst nicht „abbrechen“: hochgeladen wird schon, also
        // bleibt es in den Medien -- und das sagt der Knopf auch.
        onClose={() => {
          setAuswahl(null);
          void ablegen(lauf, dateien, null);
        }}
        closeLabel="Schließen"
      >
        <GeraeteAuswahl
          dateien={dateien}
          lauf={lauf}
          kopf={zeit(stelle ?? playhead)}
          mitFilm={role !== 'viewer'}
          spaeterWohin={stelle === null}
          onLos={(reihe, ziel) => {
            setAuswahl(null);
            if (ziel === 'medien') {
              void ablegen(lauf, reihe, null);
              return;
            }
            // Ohne Stelle: das Blatt geht zu, und die Zeitleiste fragt mit
            // ihren Plus (17. Oktober) -- hochgeladen wird derweil weiter.
            if (stelle === null) {
              setPlatzieren({ art: 'geraet', lauf, reihe, namen: reihe.map((d) => d.name) });
              onClose();
              return;
            }
            void ablegen(lauf, reihe, stelle, einfuegeSpur);
            setEinfuegeStelle(null);
            onClose();
          }}
        />
      </Sheet>
    );
  }

  return (
    <Sheet
      title={zuErsetzen ? 'Womit ersetzen?' : stelle !== null ? `Einfügen bei ${zeit(stelle)}` : 'Medien'}
      onClose={() => {
        // Wer das Blatt schliesst, ohne etwas auszusuchen, hat es sich anders
        // ueberlegt -- der Clip bleibt, wie er war, und die Stelle gilt nicht
        // mehr fuers naechste Mal.
        setErsetzen(null);
        setEinfuegeStelle(null);
        onClose();
      }}
      // Der Abgleich mit dem Projektordner: dem Rohdaten-Ordner der Ablage
      // oder dem alten Box-Ordner. Bis zum 3. Oktober stand der Knopf nur bei
      // Box -- ein Projekt an einem Nextcloud-Ordner hatte keinen Weg, neue
      // Dateien aus „rohdaten“ hereinzuholen (Sascha: „wie synchronisiere ich
      // nun ein Projekt mit einem Ordner?“).
      actions={
        <>
          {/* Alles, was die Liste gerade zeigt, auf einmal (15. Oktober). */}
          {!zuErsetzen && visible.length > 0 ? (
            <button className="small" onClick={() => void zip()} disabled={zipLaeuft} data-medien-zip title="Als ZIP herunterladen">
              <Icon name="export" size={14} /> {zipLaeuft ? '…' : visible.length === assets.length ? 'Alle als ZIP' : `${visible.length} als ZIP`}
            </button>
          ) : null}
          {project?.ablage || project?.boxFolderId ? (
            <button className="small" onClick={sync} disabled={syncing} data-abgleich title="Neue Dateien aus dem Projektordner holen">
              {syncing ? (
                'Suche…'
              ) : (
                <>
                  <Icon name="sync" size={14} />{' '}
                  {project.ablage ? (project.ablage.anbieter === 'nextcloud' ? 'Nextcloud' : 'Ablage') : 'Box'} abgleichen
                </>
              )}
            </button>
          ) : null}
        </>
      }
    >
      {/* Das ganze Blatt ist ein Ziel für Dateien, die mit der Maus kommen. */}
      <div data-ablage="medien">
      <input
        ref={picker}
        type="file"
        // Everything the ingest can actually handle. On a phone this opens the
        // camera roll; `capture` is deliberately absent so it is a choice
        // between filming now and picking something already shot.
        accept={DATEIWAHL}
        multiple
        hidden
        onChange={(e) => {
          const liste = [...(e.target.files ?? [])];
          e.target.value = '';
          if (liste.length === 0) return;
          // Eine Datei an eine Stelle: dafür braucht es keine Frage. Sonst erst
          // Reihenfolge und Ziel (Sascha, 13. Oktober).
          if (zuErsetzen || (stelle !== null && liste.length === 1)) void uploadFiles(liste, zuErsetzen ? null : stelle);
          // Gleich nach der Auswahl geht es los (Sascha, 4. November) --
          // Reihenfolge und Ziel werden festgelegt, während es hochlädt.
          else setAuswahl({ dateien: liste, lauf: starteHochladen(liste) });
        }}
      />
      {/* Die Wege, auf denen Material hereinkommt, in einer Reihe und gleich
          gross (4. November: „viele irreführende Knöpfe“) -- vorher drei
          volle Breiten untereinander, und der erste sagte je nach Lage
          etwas anderes. */}
      <div className="medien-wege" data-medien-wege>
        <button
          className="primary"
          onClick={() => picker.current?.click()}
          disabled={role === 'viewer' || upload !== null}
          data-medien-geraet
        >
          <Icon name="plus" size={18} />
          <span>{upload ? 'Lädt hoch…' : stelle !== null && !zuErsetzen ? `Vom Gerät, bei ${zeit(stelle)}` : 'Vom Gerät'}</span>
        </button>
        {/* Speaking over the cut is the other way sound gets in, and on a
            phone the more common one. */}
        <button onClick={onRecord} disabled={role === 'viewer'} data-medien-voiceover>
          <Icon name="mic" size={18} />
          <span>Voiceover</span>
        </button>
        {/* Und der dritte Weg (7.11): etwas, das schon in der Ablage liegt --
            ein Interview aus der letzten Folge, das Musikbett, eine
            Bauchbinde. Steht nur da, wo es eine Ablage gibt. */}
        {project?.ablage ? (
          <button onClick={() => setHolen(true)} disabled={role === 'viewer'} data-medien-holen>
            <Icon name="sync" size={18} />
            <span>Aus anderem Ordner</span>
          </button>
        ) : null}
      </div>
      {holen && project ? (
        <MedienHolenSheet
          projectId={project.id}
          onClose={() => setHolen(false)}
          onFertig={async () => {
            setHolen(false);
            await refreshAssets();
          }}
        />
      ) : null}
      {upload ? (
        <div style={{ marginBottom: 14 }}>
          <div className="sub" style={{ marginBottom: 6 }}>
            {upload.total > 1 ? `Datei ${upload.done + 1} von ${upload.total}: ` : ''}
            {upload.name} — {Math.round(upload.fraction * 100)}%
          </div>
          <div className="progressbar">
            <div style={{ width: `${Math.round(upload.fraction * 100)}%` }} />
          </div>
        </div>
      ) : null}
      {/* Das Suchfeld steht über den Chips: erst „welche Datei“, dann „welche
          Art“. Umgekehrt sucht man in einer Auswahl, die man gerade selbst
          eingeschränkt hat, und findet nichts. */}
      <div className="medien-suche">
        <Icon name="suchen" size={14} />
        <input
          type="search"
          value={suche}
          placeholder="In Medien suchen…"
          aria-label="In Medien suchen"
          data-medien-suche
          onChange={(e) => setSuche(e.target.value)}
        />
        {suche ? (
          <button className="ghost" data-suche-leeren aria-label="Suche leeren" onClick={() => setSuche('')}>
            ×
          </button>
        ) : null}
      </div>
      <div className="chips" style={{ marginBottom: 14 }}>
        {(['all', 'video', 'audio', 'image'] as const).map((k) => (
          <button key={k} className={`chip ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>
            {{ all: 'Alle', video: 'Video', audio: 'Ton', image: 'Bilder' }[k]}
          </button>
        ))}
      </div>

      {/* Eine Diashow (S23). Sie steht hier, weil hier die Bilder liegen --
          und nur, wenn es mehr als eines gibt: bei einem Bild ist „einfügen“
          der kürzere Weg, und ein Knopf, der dasselbe umständlicher tut, ist
          keiner. Nicht beim Ersetzen und nicht beim Einfügen an eine Stelle:
          dort ist gerade eine andere Frage offen. */}
      {!zuErsetzen && stelle === null && bilderFertig.length > 1 ? (
        <button
          style={{ width: '100%', marginBottom: 14 }}
          data-diashow-oeffnen
          onClick={onDiashow}
          disabled={role === 'viewer'}
        >
          Diashow aus {bilderFertig.length} Bildern…
        </button>
      ) : null}

      {waiting ? <WorkerWarning alive={worker.alive} lastError={worker.lastError} /> : null}

      {visible.length === 0 ? (
        /* Drei leere Zustände und nicht einer: „nichts gefunden“ heisst etwas
           anderes als „noch nichts da“, und der Knopf darunter ist ein
           anderer. Die Suche steht dabei vor dem Filter -- wer gerade getippt
           hat, sucht den Grund dort. */
        <LeererZustand
          zeichen="film"
          satz={
            gesucht
              ? `Nichts gefunden zu „${suche.trim()}“.`
              : filter === 'all'
                ? 'Noch kein Material da.'
                : 'Nichts von dieser Art da.'
          }
          dazu={
            gesucht
              ? filter === 'all'
                ? 'Gesucht wird im Dateinamen.'
                : 'Gesucht wird im Dateinamen — und daneben steht noch ein Filter für die Art.'
              : filter !== 'all'
                ? 'Oben steht ein Filter — „Alle“ zeigt wieder alles.'
                : project?.boxFolderId
                  ? 'Lade etwas hoch, oder hol dir mit „Box“ oben, was schon in der Ablage liegt.'
                  : 'Video, Ton oder Bild — es wird aufbereitet und liegt danach hier.'
          }
          knopf={
            gesucht
              ? { text: 'Suche leeren', onClick: () => setSuche('') }
              : filter !== 'all'
                ? { text: 'Alle zeigen', onClick: () => setFilter('all') }
                : { text: 'Material hochladen', onClick: () => picker.current?.click() }
          }
          kennung="medien"
        />
      ) : null}

      {/* 7.25c: Am grossen Schirm als Kachelraster statt als Liste. Eine Liste
          ist auf dem Telefon richtig -- eine Zeile je Datei, alles lesbar.
          In einer 360 Punkte breiten Spalte am Schreibtisch ist sie
          Platzverschwendung: zwei Kacheln nebeneinander zeigen doppelt so
          viel Material, ohne dass etwas kleiner wird, was man lesen muss. */}
      <div className="list medien-kacheln">
        {visible.map((asset) => (
          <div className="row media-row" key={asset.id} data-asset={asset.id}>
            {/* Ein Tipp aufs Bild zeigt es ganz, bevor es in den Film kommt. */}
            <button
              className="thumb-knopf"
              aria-label={`${asset.name} ansehen`}
              disabled={!vorschauQuelle(asset)}
              onClick={() => setAnsehen(asset)}
              data-ansehen={asset.id}
            >
              {asset.posterUrl ? (
                <img className="thumb" src={asset.posterUrl} alt="" loading="lazy" />
              ) : (
                <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 18 }}>
                  {asset.kind === 'audio' ? '♪' : asset.kind === 'image' ? '🖼' : '🎬'}
                </div>
              )}
            </button>
            <div className="grow" style={{ minWidth: 0 }}>
              {renaming?.id === asset.id ? (
                <input
                  className="rename"
                  autoFocus
                  value={renaming.value}
                  onChange={(e) => setRenaming({ id: asset.id, value: e.target.value })}
                  onBlur={() => void rename(asset, renaming.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void rename(asset, renaming.value);
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  aria-label="Name"
                />
              ) : (
                <div className="title">{asset.name}</div>
              )}
              <div className="sub">
                <StatusBadge asset={asset} />{' '}
                {/* Gespräch oder B-Roll, beim Aufbereiten erkannt (4. November). */}
                {asset.status === 'ready' && asset.hasAudio && asset.kind !== 'image' && !istGespraech(asset.sprachanteil) ? (
                  <span className="badge" data-broll title="Beim Aufbereiten keine Sprache gefunden — der KI-Schnitt lässt es von selbst aus">
                    B-Roll
                  </span>
                ) : null}{' '}
                {asset.status === 'ready'
                  ? `${asset.durationSec.toFixed(1)}s${asset.width ? ` · ${asset.width}×${asset.height}` : ''}${asset.hasAudio ? ' · Ton' : ''}`
                  : null}
              </div>
              {details === asset.id ? <Details asset={asset} /> : null}
              {asset.error ? <Reason text={asset.error} /> : null}
              {confirmDelete === asset.id ? (
                <div className="chips" style={{ marginTop: 6 }} data-confirm-delete>
                  <button className="chip danger" onClick={() => void remove(asset)} disabled={busy}>
                    Wirklich löschen
                  </button>
                  <button className="chip" onClick={() => setConfirmDelete(null)}>
                    Abbrechen
                  </button>
                </div>
              ) : null}
            </div>
            {asset.status === 'failed' || isStale(asset) ? (
              <button className="small" onClick={() => void reingest(asset)}>
                Erneut
              </button>
            ) : (
              <button className="small primary" onClick={() => insert(asset)} disabled={role === 'viewer' || asset.status !== 'ready'}>
                {zuErsetzen ? 'Einsetzen' : 'Einfügen'}
              </button>
            )}
            <button
              className="small ghost media-more"
              aria-label={`Mehr zu ${asset.name}`}
              onClick={(e) => {
                const box = e.currentTarget.getBoundingClientRect();
                setMenu({ asset, x: box.right, y: box.bottom + 6 });
              }}
            >
              ⋯
            </button>
          </div>
        ))}
      </div>

      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.asset)} onClose={() => setMenu(null)} align="right" />
      ) : null}

      {ansehen && vorschauQuelle(ansehen) ? (
        <MedienVorschau
          name={ansehen.name}
          art={ansehen.kind}
          src={vorschauQuelle(ansehen)!}
          poster={ansehen.posterUrl}
          onClose={() => setAnsehen(null)}
          aktion={
            ansehen.status === 'ready' && !isStale(ansehen)
              ? { text: zuErsetzen ? 'Einsetzen' : 'Einfügen', onClick: () => insert(ansehen), aus: role === 'viewer' }
              : undefined
          }
        />
      ) : null}

      <p className="hint" style={{ marginTop: 16 }}>
        {role === 'viewer'
          ? `Länge des Schnitts: ${duration.toFixed(1)} s.`
          : 'Nach „Einfügen“ tippen Sie im Schnitt auf ein +, wohin es soll.'}{' '}
        Dateien lassen sich auch mit der Maus hierher oder auf die Zeitleiste ziehen.
      </p>
      </div>
    </Sheet>
  );
}

/** What the file actually is: the numbers somebody debugging a cut asks for. */
function Details({ asset }: { asset: AssetView }): ReactElement {
  const mb = asset.sizeBytes ? `${(asset.sizeBytes / 1024 / 1024).toFixed(1)} MB` : null;
  const lines = [
    ['Art', { video: 'Video', audio: 'Ton', image: 'Bild' }[asset.kind]],
    ['Länge', asset.durationSec ? `${asset.durationSec.toFixed(2)} s` : null],
    ['Bild', asset.width ? `${asset.width} × ${asset.height}${asset.fps ? ` · ${asset.fps.toFixed(0)} Bilder/s` : ''}` : null],
    ['Ton', asset.kind === 'image' ? null : asset.hasAudio ? 'ja' : 'nein'],
    ['Größe', mb],
    // „Aus Box: nein, hochgeladen“ stand an jeder Datei (7.159). Gesagt wird
    // es nur noch, wo es stimmt.
    ['Aus Box', asset.boxFileId ? asset.boxFileId : null],
    ['Zuletzt', asset.updatedAt ? new Date(asset.updatedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : null],
  ].filter(([, value]) => value) as [string, string][];
  return (
    <div className="sub media-details" data-details>
      {lines.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <b>{value}</b>
        </div>
      ))}
    </div>
  );
}

/**
 * Why a file failed. The first line is the summary the worker put there; the
 * rest is ffmpeg's own output, which is worth having and not worth reading
 * unless somebody is actually debugging.
 */
function Reason({ text }: { text: string }): ReactElement {
  const [open, setOpen] = useState(false);
  const [first, ...rest] = text.split('\n');
  return (
    <div className="sub" style={{ color: 'var(--err-text)' }}>
      <div style={{ whiteSpace: 'pre-wrap' }}>{open ? text : first}</div>
      {rest.length > 0 ? (
        <button className="small ghost" style={{ padding: '4px 0' }} onClick={() => setOpen(!open)}>
          {open ? 'Weniger' : 'Mehr dazu'}
        </button>
      ) : null}
    </div>
  );
}

/** "Ingesting" for longer than ten minutes is not slow, it is stuck. */
function isStale(asset: AssetView): boolean {
  if (asset.status === 'ready' || asset.status === 'failed' || !asset.updatedAt) return false;
  return Date.now() - Date.parse(asset.updatedAt) > 10 * 60_000;
}

function StatusBadge({ asset }: { asset: AssetView }): ReactElement {
  if (asset.status === 'ready') return <span className="badge ok">bereit</span>;
  if (asset.status === 'failed') return <span className="badge err">Fehler</span>;
  if (isStale(asset)) return <span className="badge busy">hängt — Erneut versuchen</span>;
  return <span className="badge busy">wird aufbereitet…</span>;
}

/**
 * Woraus die Vorschau spielt: beim Video und Ton die Arbeitskopie, beim Bild
 * das Original. Steht noch nichts bereit, gibt es nichts anzusehen.
 */
function vorschauQuelle(asset: AssetView): string | null {
  if (asset.kind === 'image') return asset.originalUrl ?? asset.posterUrl;
  return asset.proxyUrl ?? asset.originalUrl;
}
